import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { getAppInstallStats } from "../../api/project";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

/**
 * GET /admin/app-install/stats?from=YYYY-MM-DD&to=YYYY-MM-DD
 * AjaxResult.data:
 * {
 *   summary: [
 *     {app_kbn:'jm', os_kbn:'ios', total_device_count:1200,
 *      total_install_count:1300, total_reinstall_count:100}
 *   ],
 *   daily: [
 *     {install_date:'2026-10-25', app_kbn:'jm', os_kbn:'ios',
 *      new_install_count:10, reinstall_count:3, reinstall_device_count:2}
 *   ]
 * }
 * summary = lifetime totals, unaffected by selected date range.
 * daily = selected range only.
 * reinstall_count = number of reinstall events; reinstall_device_count = distinct devices.
 */
const GROUPS = [
  { app: "jm", os: "ios", name: "雀活マスター", platform: "iOS" },
  { app: "jm", os: "android", name: "雀活マスター", platform: "Android" },
  { app: "oshical", os: "ios", name: "推しごとカレンダー", platform: "iOS" },
  {
    app: "oshical",
    os: "android",
    name: "推しごとカレンダー",
    platform: "Android",
  },
];
const zero = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const keyOf = (app, os) =>
  `${String(app).toLowerCase()}:${String(os).toLowerCase()}`;
const pad = (n) => String(n).padStart(2, "0");
const dateText = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const localDate = (text) => new Date(`${text}T00:00:00`);
const rangeValidation = (from, to) => {
  if (!from || !to) return "開始日と終了日を指定してください。";
  if (from > to) return "開始日は終了日以前を指定してください。";
  const days =
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
      86400000 +
    1;
  if (!Number.isFinite(days)) return "有効な日付を指定してください。";
  return days > 366 ? "対象期間は366日以内で指定してください。" : "";
};
const numberText = (v) => zero(v).toLocaleString("ja-JP");
const metricStyle = {
  padding: 16,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  background: "#fff",
};

export default function AppInstallDashboard() {
  const [to, setTo] = useState(() => dateText(new Date()));
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return dateText(d);
  });
  const [selectedGroup, setSelectedGroup] = useState("jm:ios");
  // Preserve visibility choices across navigation and page reloads (same browser).
  const [visibleApps, setVisibleApps] = useState(() => {
    try {
      const saved = JSON.parse(
        window.localStorage.getItem("appInstall.overall.visibleApps.v1") ||
          "null",
      );
      if (
        saved &&
        typeof saved.jm === "boolean" &&
        typeof saved.oshical === "boolean"
      ) {
        return { jm: saved.jm, oshical: saved.oshical };
      }
    } catch (_) {
      /* localStorage may be unavailable */
    }
    return { jm: true, oshical: true };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "appInstall.overall.visibleApps.v1",
        JSON.stringify(visibleApps),
      );
    } catch (_) {
      /* storage disabled */
    }
  }, [visibleApps]);
  const toggleApp = (app) =>
    setVisibleApps((prev) => ({ ...prev, [app]: !prev[app] }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const rangeError = rangeValidation(from, to);
  const ready = data !== null && !loading && !error && !rangeError;

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const response = await getAppInstallStats({ from, to }, signal);
        const result = response?.data;
        if (!Array.isArray(result?.summary) || !Array.isArray(result?.daily)) {
          throw new Error("取得データの形式が正しくありません");
        }
        if (!signal.aborted) {
          setData({
            summary: result.summary,
            daily: result.daily,
          });
        }
      } catch (err) {
        if (!signal.aborted)
          setError(`データ取得に失敗しました: ${err.message}`);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [from, to],
  );

  useEffect(() => {
    if (rangeError) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [rangeError, load, reload]);

  const summaryByGroup = useMemo(() => {
    const map = new Map();
    for (const row of data?.summary ?? [])
      map.set(keyOf(row.app_kbn, row.os_kbn), row);
    return map;
  }, [data]);

  const dailyByGroup = useMemo(() => {
    const map = new Map();
    for (const row of data?.daily ?? []) {
      const key = keyOf(row.app_kbn, row.os_kbn);
      const prev = map.get(key) ?? [];
      prev.push(row);
      map.set(key, prev);
    }
    return map;
  }, [data]);

  const displayRows = useMemo(
    () =>
      GROUPS.map((group) => {
        const key = keyOf(group.app, group.os);
        const rows = dailyByGroup.get(key) ?? [];
        const all = summaryByGroup.get(key) ?? {};
        return {
          ...group,
          key,
          lifetimeDevices: zero(all.total_device_count),
          lifetimeInstalls: zero(all.total_install_count),
          lifetimeReinstalls: zero(all.total_reinstall_count),
          newInstalls: rows.reduce((n, r) => n + zero(r.new_install_count), 0),
          reinstallEvents: rows.reduce(
            (n, r) => n + zero(r.reinstall_count),
            0,
          ),
          // Count of distinct devices within each day; summed over days.
          // A device reinstalled on two days is counted twice here.
          reinstallDeviceDays: rows.reduce(
            (n, r) => n + zero(r.reinstall_device_count),
            0,
          ),
        };
      }),
    [dailyByGroup, summaryByGroup],
  );

  // Overall totals are sums of four app/OS groups. A device used on multiple
  // apps is counted once per app; this is not a cross-app unique-device count.
  const overall = useMemo(
    () =>
      displayRows.reduce(
        (acc, row) => ({
          lifetimeDevices: acc.lifetimeDevices + row.lifetimeDevices,
          lifetimeInstalls: acc.lifetimeInstalls + row.lifetimeInstalls,
          lifetimeReinstalls: acc.lifetimeReinstalls + row.lifetimeReinstalls,
          newInstalls: acc.newInstalls + row.newInstalls,
          reinstallEvents: acc.reinstallEvents + row.reinstallEvents,
        }),
        {
          lifetimeDevices: 0,
          lifetimeInstalls: 0,
          lifetimeReinstalls: 0,
          newInstalls: 0,
          reinstallEvents: 0,
        },
      ),
    [displayRows],
  );

  const overallChartData = useMemo(() => {
    if (rangeError) return [];
    const byDate = new Map();
    for (const item of data?.daily ?? []) {
      const app = String(item.app_kbn || "").toLowerCase();
      const os = String(item.os_kbn || "").toLowerCase();
      if (!(["jm", "oshical"].includes(app) && ["ios", "android"].includes(os)))
        continue;
      const day = String(item.install_date).slice(0, 10);
      const current = byDate.get(day) ?? { jm: 0, oshical: 0 };
      current[app] += zero(item.new_install_count) + zero(item.reinstall_count);
      byDate.set(day, current);
    }
    let jmCumulative = 0;
    let oshicalCumulative = 0;
    const rows = [];
    for (
      let d = localDate(from);
      d <= localDate(to);
      d.setDate(d.getDate() + 1)
    ) {
      const date = dateText(d);
      const current = byDate.get(date) ?? { jm: 0, oshical: 0 };
      jmCumulative += current.jm;
      oshicalCumulative += current.oshical;
      rows.push({ date, jmCumulative, oshicalCumulative });
    }
    return rows;
  }, [data, from, to, rangeError]);

  const selected =
    GROUPS.find((g) => keyOf(g.app, g.os) === selectedGroup) ?? GROUPS[0];
  const chartData = useMemo(() => {
    const byDate = new Map(
      (dailyByGroup.get(selectedGroup) ?? []).map((r) => [r.install_date, r]),
    );
    if (rangeError) return [];
    const start = localDate(from);
    const end = localDate(to);
    const rows = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = dateText(d);
      const row = byDate.get(day) ?? {};
      rows.push({
        date: day,
        newInstall: zero(row.new_install_count),
        reinstall: zero(row.reinstall_count),
      });
    }
    return rows;
  }, [dailyByGroup, selectedGroup, from, to, rangeError]);

  const downloadCsv = () => {
    if (!ready || !data.daily.length) return;
    const header = [
      "日付",
      "アプリ",
      "OS",
      "新規インストール数",
      "再インストール回数",
      "再インストール端末数",
    ];
    const labels = new Map(GROUPS.map((g) => [keyOf(g.app, g.os), g]));
    const rows = [...data.daily]
      .sort((a, b) =>
        String(a.install_date).localeCompare(String(b.install_date)),
      )
      .map((r) => {
        const label = labels.get(keyOf(r.app_kbn, r.os_kbn));
        return [
          r.install_date,
          label?.name ?? r.app_kbn,
          label?.platform ?? r.os_kbn,
          zero(r.new_install_count),
          zero(r.reinstall_count),
          zero(r.reinstall_device_count),
        ];
      });
    const esc = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv =
      "\ufeff" +
      [header, ...rows].map((row) => row.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `app-install-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <main
        style={{
          background: "#f8fafc",
          minHeight: "100%",
          padding: "24px 0",
          color: "#172033",
          textAlign: "left",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <header
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "end",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: 24 }}>
                アプリインストール状況
              </h1>
              <p style={{ color: "#64748b", margin: "6px 0 0" }}>
                FCM登録データから集計した推定インストール状況
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "end",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <label style={{ display: "grid", gap: 4, fontSize: 13 }}>
                開始日
                <input
                  aria-label="開始日"
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4, fontSize: 13 }}>
                終了日
                <input
                  aria-label="終了日"
                  type="date"
                  value={to}
                  min={from}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={downloadCsv}
                disabled={!ready || !data?.daily.length}
                style={{ padding: "8px 12px", cursor: "pointer" }}
              >
                CSV出力
              </button>
              <button
                type="button"
                onClick={() => setReload((value) => value + 1)}
                disabled={loading || !!rangeError}
                style={{ padding: "8px 12px" }}
              >
                再読み込み
              </button>
            </div>
          </header>
          {rangeError && (
            <p role="alert" style={{ color: "#b91c1c" }}>
              {rangeError}
            </p>
          )}
          {error && (
            <p role="alert" style={{ color: "#b91c1c" }}>
              {error}
            </p>
          )}
          {ready && (
            <>
              {!data.daily.length && (
                <p role="status">対象期間のインストールデータはありません。</p>
              )}
              <section
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(235px, 1fr))",
                  gap: 14,
                  marginBottom: 22,
                }}
              >
                {displayRows.map((r) => (
                  <article
                    key={r.key}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedGroup === r.key}
                    aria-label={`${r.name} / ${r.platform}の推移を表示`}
                    style={{
                      ...metricStyle,
                      border:
                        selectedGroup === r.key
                          ? "2px solid #2563eb"
                          : metricStyle.border,
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedGroup(r.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedGroup(r.key);
                      }
                    }}
                  >
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {r.name} / {r.platform}
                    </div>
                    <div
                      style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}
                    >
                      {numberText(r.lifetimeDevices)}{" "}
                      <span style={{ fontSize: 12, fontWeight: 400 }}>
                        端末
                      </span>
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}
                    >
                      累計登録端末数（重複除外）
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        borderTop: "1px solid #e5e7eb",
                        marginTop: 12,
                        paddingTop: 12,
                        fontSize: 13,
                      }}
                    >
                      <span>
                        期間内新規 <strong>{numberText(r.newInstalls)}</strong>
                      </span>
                      <span>
                        期間内再インストール{" "}
                        <strong>{numberText(r.reinstallEvents)}</strong>
                      </span>
                    </div>
                  </article>
                ))}
              </section>
              <section style={{ ...metricStyle, marginBottom: 18 }}>
                <h2 style={{ margin: "0 0 6px", fontSize: 17 }}>
                  全アプリ合計
                </h2>
                <p
                  style={{ color: "#64748b", fontSize: 12, margin: "0 0 16px" }}
                >
                  雀活マスター・推しごとカレンダーのiOS／Android、計4区分の合算
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  {[
                    ["累計登録端末数", overall.lifetimeDevices],
                    ["累計インストール回数", overall.lifetimeInstalls],
                    ["期間内新規", overall.newInstalls],
                    ["期間内再インストール", overall.reinstallEvents],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        border: "1px solid #e5e7eb",
                        background: "#f8fafc",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        {label}
                      </div>
                      <div
                        style={{ fontSize: 24, fontWeight: 700, marginTop: 5 }}
                      >
                        {numberText(value)}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 6,
                  }}
                >
                  <h3 style={{ fontSize: 14, margin: 0 }}>
                    アプリ別インストール累計推移
                  </h3>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      { app: "jm", label: "雀活マスター", color: "#2563eb" },
                      {
                        app: "oshical",
                        label: "推しごとカレンダー",
                        color: "#00C49F",
                      },
                    ].map((option) => (
                      <button
                        key={option.app}
                        type="button"
                        aria-pressed={visibleApps[option.app]}
                        onClick={() => toggleApp(option.app)}
                        style={{
                          padding: "7px 11px",
                          borderRadius: 8,
                          border: `1px solid ${visibleApps[option.app] ? option.color : "#cbd5e1"}`,
                          background: visibleApps[option.app]
                            ? option.color
                            : "#fff",
                          color: visibleApps[option.app] ? "#fff" : "#475569",
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                      >
                        {option.label}：
                        {visibleApps[option.app] ? "表示" : "非表示"}
                      </button>
                    ))}
                  </div>
                </div>
                <p
                  style={{ color: "#64748b", fontSize: 12, margin: "0 0 12px" }}
                >
                  各アプリのiOS＋Android合計。期間内の新規・再インストール回数を日別に累積しています。
                </p>
                <div style={{ width: "100%", height: 310 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={overallChartData}
                      margin={{ top: 8, right: 18, left: -15, bottom: 12 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        minTickGap={20}
                      />
                      <YAxis allowDecimals={false} domain={[0, "auto"]} />
                      <Tooltip formatter={(value) => numberText(value)} />
                      <Legend />
                      {visibleApps.jm && (
                        <Line
                          type="monotone"
                          dataKey="jmCumulative"
                          name="雀活マスター"
                          stroke="#2563eb"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 5 }}
                        />
                      )}
                      {visibleApps.oshical && (
                        <Line
                          type="monotone"
                          dataKey="oshicalCumulative"
                          name="推しごとカレンダー"
                          stroke="#00C49F"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 5 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {!visibleApps.jm && !visibleApps.oshical && (
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    両アプリが非表示です。表示するアプリを選択してください。
                  </p>
                )}
                <p
                  style={{ color: "#64748b", fontSize: 12, margin: "10px 0 0" }}
                >
                  ※
                  選択期間の開始日を0として積算したインストール回数（再インストールを含む）です。表示／非表示の選択はこのブラウザに保存されます。
                </p>
              </section>
              <section style={{ ...metricStyle, marginBottom: 18 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: 17 }}>
                    日別インストール推移
                  </h2>
                  <select
                    aria-label="グラフ対象"
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    {GROUPS.map((g) => (
                      <option
                        key={keyOf(g.app, g.os)}
                        value={keyOf(g.app, g.os)}
                      >
                        {g.name} / {g.platform}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    color: "#64748b",
                    fontSize: 12,
                    margin: "8px 0 14px",
                  }}
                >
                  {selected.name} / {selected.platform} ・
                  初回登録と再インストール推定回数
                </div>
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 8, right: 10, left: -15, bottom: 12 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        minTickGap={20}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="newInstall"
                        name="新規"
                        fill="#2563eb"
                        radius={[3, 3, 0, 0]}
                      />
                      <Bar
                        dataKey="reinstall"
                        name="再インストール（推定）"
                        fill="#f59e0b"
                        radius={[3, 3, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
              <section style={metricStyle}>
                <h2 style={{ marginTop: 0, fontSize: 17 }}>アプリ・OS別集計</h2>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      borderCollapse: "collapse",
                      width: "100%",
                      minWidth: 850,
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f1f5f9" }}>
                        {[
                          "アプリ",
                          "OS",
                          "累計端末数",
                          "累計インストール回数",
                          "累計再インストール回数",
                          "期間内新規",
                          "期間内再インストール回数",
                          "期間内再インストール端末日数",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "12px 10px",
                              textAlign: "right",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((r) => (
                        <tr key={r.key}>
                          {[
                            r.name,
                            r.platform,
                            r.lifetimeDevices,
                            r.lifetimeInstalls,
                            r.lifetimeReinstalls,
                            r.newInstalls,
                            r.reinstallEvents,
                            r.reinstallDeviceDays,
                          ].map((v, i) => (
                            <td
                              key={i}
                              style={{
                                padding: "12px 10px",
                                textAlign: i < 2 ? "left" : "right",
                                borderBottom: "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {i < 2 ? v : numberText(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ color: "#64748b", fontSize: 12, marginBottom: 0 }}>
                  ※
                  件数はFCM登録を基にした推計値です。アンインストール数やストアのダウンロード数を直接表すものではありません。「端末日数」は日ごとのユニーク端末数の合計です。
                </p>
              </section>
            </>
          )}
          {loading && (
            <p role="status" style={{ color: "#64748b" }}>
              読み込み中...
            </p>
          )}
        </div>
      </main>
    </MainLayout>
  );
}
